const AUTH_STORAGE_KEY = 'spec_auth';

/**
 * Clear stored auth and dispatch event to notify the app
 * This is used when we detect an expired/invalid token
 */
const clearAuthAndNotify = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  // Dispatch a custom event that AuthContext can listen for
  window.dispatchEvent(new CustomEvent('auth:expired'));
};

/**
 * Get the current user's auth token from localStorage
 * Returns null if no user is authenticated
 */
export const getAuthToken = async (): Promise<string | null> => {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return null;
    const auth = JSON.parse(stored);
    return auth.token || null;
  } catch {
    return null;
  }
};

/**
 * Get headers for authenticated API requests
 * Throws error if no user is authenticated
 */
export const getAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('No authenticated user. Please sign in.');
  }

  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };
};

/**
 * Get headers for API requests - includes auth if available, doesn't fail without it
 */
export const getOptionalAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Make an authenticated fetch request (requires login)
 * Automatically handles 401 responses by clearing auth
 */
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const headers = await getAuthHeaders();

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  // If we get a 401, the token is expired/invalid - clear auth
  if (response.status === 401) {
    clearAuthAndNotify();
  }

  return response;
};

/**
 * Make a fetch request with optional auth (works without login)
 */
export const optionalAuthFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const headers = await getOptionalAuthHeaders();

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });
};
